import { Button, Segmented, Skeleton, Tooltip } from 'antd'
import dayjs from 'dayjs'
import type { ReactNode } from 'react'
import { MdArrowDownward, MdArrowForward, MdArrowUpward, MdStar } from 'react-icons/md'
import { Link } from 'react-router-dom'

import { cx } from '@/Ship/Helpers/cssClass'
import type { DashboardPerformance, DashboardPeriod, TrendPoint } from '@/Ship/Contracts/DashboardSummary'
import { useMobileMode } from '@/Ship/Hooks/useMobileMode'
import listStyles from '@/Ship/Templates/CrudResource/CrudResourceTemplate.module.css'

import { axisLabelDates, countDelta, delayedShare, tasksWord, valueDelta, type Delta } from '../Helpers/dashboardFormat'

import styles from './Dashboard.module.css'

const PERIOD_OPTIONS: { value: DashboardPeriod; label: string }[] = [
    { value: 'today', label: 'Сьогодні' },
    { value: 'week', label: 'Тиждень' },
    { value: 'month', label: 'Місяць' },
]

/** З чим порівнюємо — для підказки біля дельти */
const PREVIOUS_LABELS: Record<DashboardPeriod, string> = {
    today: 'до вчора на цей час',
    week: 'до таких самих днів минулого тижня',
    month: 'до таких самих днів минулого місяця',
}

const WEEKDAYS = ['нд', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб']

/** Скільки днів динаміки показувати: на телефоні 30 стовпчиків — по 10px, у них не влучити */
const TREND_DAYS = { desktop: 30, phone: 14 }
/** Ширина, з якої сторінка вважається телефонною (як у медіа-запитах Dashboard.module.css) */
const PHONE_MAX_WIDTH = 575

const DeltaBadge = ({ delta, hint }: { delta: Delta; hint: string }) => {
    if (delta.text === null)
        return (
            <Tooltip title="Немає з чим порівняти: у попередньому відрізку 0">
                <span
                    className={`${cx(styles, 'delta')} ${cx(styles, 'deltaNone')}`}
                    aria-label="Немає з чим порівняти"
                >
                    —
                </span>
            </Tooltip>
        )
    const tone = delta.tone === 'good' ? 'deltaGood' : delta.tone === 'bad' ? 'deltaBad' : 'deltaBase'
    const Icon = delta.direction === 'up' ? MdArrowUpward : delta.direction === 'down' ? MdArrowDownward : null
    return (
        <Tooltip title={hint}>
            <span className={`${cx(styles, 'delta')} ${cx(styles, tone)}`} aria-label={`${delta.text}, ${hint}`}>
                {Icon && <Icon aria-hidden="true" />}
                {delta.text}
            </span>
        </Tooltip>
    )
}

interface Kpi {
    key: string
    label: string
    value: ReactNode
    delta: Delta
    hint: string
}

interface PerformancePanelProps {
    performance: DashboardPerformance | null
    /** Динаміка за 30 днів — окремо від показників: від періоду не залежить, тому не зникає при його зміні */
    trend: TrendPoint[] | null
    period: DashboardPeriod
    onPeriodChange: (period: DashboardPeriod) => void
    loading: boolean
    error: string | null
    onRetry: () => void
    /** Посилання на звіт — лише тим, кому звіти доступні */
    showReportLink: boolean
}

/**
 * «Показники»: підтверджені диспетчером задачі за період (сьогодні / тиждень / місяць) зі
 * зміною до такого самого відрізку попереднього періоду і стовпчики виконаних задач по
 * днях за 30 днів. Числа рахує той самий код, що й звіт «Виконання задач».
 */
export const PerformancePanel = ({
    performance,
    trend: trendAll,
    period,
    onPeriodChange,
    loading,
    error,
    onRetry,
    showReportLink,
}: PerformancePanelProps) => {
    const current = performance?.current
    const previous = performance?.previous
    const hint = `Зміна ${PREVIOUS_LABELS[period]}`
    const share = current ? delayedShare(current.delayed, current.tasks) : null
    const previousShare = previous ? delayedShare(previous.delayed, previous.tasks) : null
    const number = (value: number, digits = 0) => value.toLocaleString('uk-UA', { maximumFractionDigits: digits })

    const kpis: Kpi[] =
        current && previous
            ? [
                  {
                      key: 'tasks',
                      label: 'Виконано задач',
                      value: number(current.tasks),
                      delta: countDelta(current.tasks, previous.tasks),
                      hint,
                  },
                  {
                      key: 'trips',
                      label: 'Ходок',
                      value: number(current.trips),
                      delta: countDelta(current.trips, previous.trips),
                      hint,
                  },
                  {
                      key: 'tonnes',
                      label: 'Перевезено, т',
                      value: number(current.tonnes, 1),
                      delta: countDelta(current.tonnes, previous.tonnes, true, 1),
                      hint,
                  },
                  {
                      key: 'delayed',
                      label: 'Із затримкою',
                      value: share === null ? '—' : `${number(share, 1)}%`,
                      delta: valueDelta(share, previousShare, { higherIsBetter: false, suffix: ' в. п.' }),
                      hint: `${hint} (у відсоткових пунктах)`,
                  },
                  {
                      key: 'rating',
                      label: 'Середня оцінка',
                      value:
                          current.averageRating === null ? (
                              '—'
                          ) : (
                              <>
                                  <MdStar className={cx(styles, 'kpiStar')} aria-hidden="true" />
                                  {number(current.averageRating, 1)}
                              </>
                          ),
                      delta: valueDelta(current.averageRating, previous.averageRating),
                      hint,
                  },
              ]
            : []

    const phone = useMobileMode(PHONE_MAX_WIDTH)
    const days = phone ? TREND_DAYS.phone : TREND_DAYS.desktop
    // Бекенд віддає дні за зростанням; на телефоні — хвіст ряду
    const trend = (trendAll ?? []).slice(-days)
    const trendLoading = trendAll === null
    const shownDays = trend.length || days
    const maxTasks = Math.max(1, ...trend.map((point) => point.tasks))
    const trendTotal = trend.reduce((sum, point) => sum + point.tasks, 0)
    const today = dayjs().format('YYYY-MM-DD')
    const labelled = axisLabelDates(
        trend.map((point) => point.date),
        today
    )
    const columns = { gridTemplateColumns: `repeat(${shownDays}, minmax(0, 1fr))` }
    const trendSummary = `Разом ${tasksWord(trendTotal)}${trendTotal > 0 ? ` · макс. ${maxTasks} на день` : ''}`

    return (
        <section className={cx(listStyles, 'card')} aria-labelledby="dashboard-performance-title">
            <header className={cx(listStyles, 'header')}>
                <div className={cx(listStyles, 'headerMain')}>
                    <h2 id="dashboard-performance-title" className={cx(styles, 'sectionTitle')}>
                        Показники
                    </h2>
                    <div className={cx(listStyles, 'subtitle')}>Задачі, підтверджені диспетчером</div>
                </div>
                <div className={cx(listStyles, 'headerActions')}>
                    <Segmented<DashboardPeriod> options={PERIOD_OPTIONS} value={period} onChange={onPeriodChange} />
                    {showReportLink && (
                        <Link className={cx(styles, 'headerLink')} to="/reports/completion">
                            Звіт <MdArrowForward aria-hidden="true" />
                        </Link>
                    )}
                </div>
            </header>

            {error && !performance && !loading && trendLoading ? (
                <div className={cx(styles, 'errorLine')} role="alert">
                    {error}
                    <Button size="small" onClick={onRetry}>
                        Повторити
                    </Button>
                </div>
            ) : (
                <>
                    <div className={cx(styles, 'kpis')} aria-busy={loading}>
                        {(kpis.length > 0
                            ? kpis
                            : ['tasks', 'trips', 'tonnes', 'delayed', 'rating'].map((key) => ({ key }) as Kpi)
                        ).map((kpi) => (
                            <div key={kpi.key} className={cx(styles, 'kpi')}>
                                {kpi.label ? (
                                    <>
                                        <span className={cx(styles, 'kpiLabel')}>{kpi.label}</span>
                                        <span className={cx(styles, 'kpiValue')}>{kpi.value}</span>
                                        <DeltaBadge delta={kpi.delta} hint={kpi.hint} />
                                    </>
                                ) : (
                                    <Skeleton active paragraph={{ rows: 1 }} title={{ width: '60%' }} />
                                )}
                            </div>
                        ))}
                    </div>

                    <div className={cx(styles, 'trend')}>
                        <div className={cx(styles, 'trendHead')}>
                            <span className={cx(styles, 'trendTitle')}>Виконано задач по днях, {shownDays} днів</span>
                            {!trendLoading && <span className={cx(styles, 'trendTotal')}>{trendSummary}</span>}
                        </div>
                        <div
                            className={cx(styles, 'chart')}
                            style={columns}
                            role={trendLoading ? undefined : 'img'}
                            aria-busy={trendLoading}
                            aria-label={
                                trendLoading
                                    ? undefined
                                    : `Виконано задач по днях за ${shownDays} днів, ${trendSummary}`
                            }
                        >
                            {trend.map((point) => {
                                const date = dayjs(point.date)
                                const weekend = date.day() === 0 || date.day() === 6
                                const barClass = [
                                    cx(styles, 'bar'),
                                    // Сьогодні виділяється завжди, навіть з нулем (зранку — типово)
                                    point.date === today
                                        ? cx(styles, 'barToday')
                                        : point.tasks === 0
                                          ? cx(styles, 'barEmpty')
                                          : weekend
                                            ? cx(styles, 'barWeekend')
                                            : '',
                                ].join(' ')
                                return (
                                    <Tooltip
                                        key={point.date}
                                        title={`${WEEKDAYS[date.day()]}, ${date.format('DD.MM')}: ${tasksWord(point.tasks)}, ходок ${point.trips}`}
                                    >
                                        <span className={cx(styles, 'barCol')}>
                                            <span
                                                className={barClass}
                                                style={{ height: `${Math.max(2, (point.tasks / maxTasks) * 100)}%` }}
                                            />
                                        </span>
                                    </Tooltip>
                                )
                            })}
                        </div>
                        <div className={cx(styles, 'axis')} style={columns} aria-hidden="true">
                            {trend.map((point) => (
                                <span key={point.date}>
                                    {labelled.has(point.date) ? dayjs(point.date).format('DD.MM') : ''}
                                </span>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </section>
    )
}
